import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Simple in-memory counts cache shared across PageHeaders. Refetched every
// 60s in the background, and forcibly refreshed via `refreshCount(module)` by
// pages after mutating rows (create/delete/import).
const _cache = { data: {}, ts: 0, listeners: new Set() };
const _notify = () => _cache.listeners.forEach((cb) => cb(_cache.data));

async function _fetchCounts() {
  try {
    const { data } = await api.get("/counts");
    _cache.data = data || {};
    _cache.ts = Date.now();
    _notify();
  } catch (_) { /* silent — counts are cosmetic */ }
}

export function useCount(module) {
  const [n, setN] = useState(module ? _cache.data[module] : undefined);
  useEffect(() => {
    if (!module) return;
    const listener = (data) => setN(data[module]);
    _cache.listeners.add(listener);
    // If cache is stale (> 30s) or empty, refetch.
    if (Date.now() - _cache.ts > 30_000) _fetchCounts();
    else setN(_cache.data[module]);
    return () => _cache.listeners.delete(listener);
  }, [module]);
  return n;
}

export function refreshCounts() {
  return _fetchCounts();
}

function CountBadge({ module }) {
  const n = useCount(module);
  if (n == null) return null;
  return (
    <span data-testid={`page-count-${module}`} className="ml-2 inline-flex items-center h-4 px-1.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-mono font-semibold">
      {n.toLocaleString()}
    </span>
  );
}

export function PageHeader({ title, subtitle, children, testId, module }) {
  return (
    <div className="h-14 shrink-0 border-b border-slate-200 bg-white px-6 flex items-center justify-between">
      <div className="min-w-0">
        <div className="flex items-center">
          <h1 data-testid={testId} className="font-heading text-[16px] font-semibold tracking-tight text-slate-900">
            {title}
          </h1>
          {module && <CountBadge module={module} />}
        </div>
        {subtitle && <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

export function PageBody({ children }) {
  return <div className="flex-1 overflow-auto p-6">{children}</div>;
}

export function Toolbar({ children }) {
  return <div className="flex items-center gap-2 mb-3">{children}</div>;
}

export function Field({ label, children, span = 1, mono = false, hidden = false }) {
  // Aug 5 2026 — a field the user has no View permission on must not render at
  // all. Stripping the value server-side left an empty labelled box on screen,
  // which still tells the user the field exists.
  if (hidden) return null;
  return (
    <div style={{ gridColumn: `span ${span} / span ${span}` }}>
      <div className="field-label">{label}</div>
      <div className={mono ? "font-mono" : ""}>{children}</div>
    </div>
  );
}

export function StatusPill({ value, tone = "default" }) {
  const map = {
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-red-50 text-red-800 border-red-200",
    info: "bg-blue-50 text-blue-800 border-blue-200",
    default: "bg-slate-100 text-slate-700 border-slate-200",
  };
  if (!value) return <span className="text-slate-400">—</span>;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider rounded-sm border ${map[tone] || map.default}`}
    >
      {value}
    </span>
  );
}

export function statusTone(status) {
  if (!status) return "default";
  const s = String(status).toLowerCase();
  if (["shipped", "delivered", "received", "accepted", "eligible"].some((x) => s.includes(x))) return "success";
  if (["awaiting", "pending", "submitted", "in-stock", "added"].some((x) => s.includes(x))) return "warn";
  if (["cancel", "reject", "rto", "refund"].some((x) => s.includes(x))) return "danger";
  if (["daily-ship", "purchase", "customer"].some((x) => s.includes(x))) return "info";
  return "default";
}
